/**
 * Shared utility for determining training artifact action
 * Extracts the link-resolution logic to prevent drift across components
 */

import type { TrainingArtifact } from '@/types/training';

export type ArtifactActionKind = 'open' | 'buy' | 'details' | 'disabled';

export interface ArtifactAction {
  kind: ArtifactActionKind;
  label: string;
  url?: string;
}

/**
 * Determines the correct action for a training artifact
 * 
 * Logic:
 * - Books:
 *   - is_reference_only → disabled ("Reference Only")
 *   - purchase_url exists → kind=buy, url=purchase_url
 *   - metadata_url exists → kind=details, url=metadata_url
 *   - else → disabled ("Purchase info unavailable")
 * - Non-books:
 *   - access_url exists → kind=open, url=access_url
 *   - else → disabled ("Unavailable")
 */
export function getTrainingArtifactAction(
  artifact: TrainingArtifact,
  parentBook?: TrainingArtifact | null,
): ArtifactAction {
  const isBook = artifact.artifact_type === 'book';
  const isChapter = artifact.artifact_type === 'chapter';
  
  // Chapters inherit purchase info from parent book
  if (isChapter) {
    const effectiveArtifact = parentBook || artifact;
    if (effectiveArtifact.purchase_url) {
      return {
        kind: 'buy',
        label: `Buy from ${effectiveArtifact.purchase_provider_name ?? artifact.purchase_provider_name ?? 'Seller'}`,
        url: effectiveArtifact.purchase_url,
      };
    }
    if (effectiveArtifact.metadata_url) {
      return { kind: 'details', label: 'View Details', url: effectiveArtifact.metadata_url };
    }
    return { kind: 'disabled', label: 'Purchase info unavailable' };
  }

  if (isBook) {
    if (artifact.is_reference_only) {
      return { kind: 'disabled', label: 'Reference Only' };
    }
    if (artifact.purchase_url) {
      return { 
        kind: 'buy', 
        label: `Buy from ${artifact.purchase_provider_name ?? 'Seller'}`,
        url: artifact.purchase_url 
      };
    }
    if (artifact.metadata_url) {
      return { kind: 'details', label: 'View Details', url: artifact.metadata_url };
    }
    return { kind: 'disabled', label: 'Purchase info unavailable' };
  }
  
  // Non-books: use access_url
  if (artifact.access_url) {
    return { kind: 'open', label: 'Open', url: artifact.access_url };
  }
  
  return { kind: 'disabled', label: 'Unavailable' };
}
